/**
 * SCROLL SWEEP — the shared driver behind the homepage's full-screen text
 * panels (extracted from eg-home-panel.js, 2026-08-04, when the closing Clay
 * panel needed the same effect with different colours and more than one
 * stanza).
 *
 * ONE MECHANISM, TWO CALLERS, AND THAT IS THE POINT. The Vanilla panel sweeps
 * Toast -> Clay across one stanza; the built line takes the SEQUENCING for two
 * stanzas that hand over to each other and declares no recolour at all.
 * Everything that differs between them is a colour, a threshold or a selector —
 * i.e. a value — so it is passed in here and dialled in CSS. Nothing about the
 * effect itself is duplicated: the per-character split, the running index, the
 * rAF-coalesced progress write and the one-shot arrival all live here, once.
 *
 * A CALLER MAY IGNORE THE SPLIT, AND ONE DOES. The built line names `index` and
 * `charClass` because they are written unconditionally, but no rule in
 * section-eg-home-builtline.css reads them, so its characters are inert inline
 * carriers. That is cheaper than a second scroll driver in this repo, and it is
 * why nothing in this file has a design value in it.
 *
 * Does exactly four things, and deliberately nothing else:
 *
 *   1. SPLITS each GROUP's lines into one <span> per character, tagging each
 *      with its index within that group (`o.index`) and writing the group's
 *      character count (`o.count`) onto the group.
 *   2. NUMBERS the groups (`o.groupIndex` / `o.groupCount`) so CSS can give
 *      each one its own slice of the scroll without this file knowing how the
 *      slices are cut.
 *   3. WRITES `o.progress`, the host's scroll progress, onto the host element
 *      on every animation frame the page scrolls.
 *   4. FIRES ONCE when the host reaches the top of the window, which starts the
 *      copy's fade. That beat is on a CLOCK, not on the wheel.
 *
 * Every threshold, colour and easing decision stays in the CALLING section's
 * stylesheet, against those properties. This file has no design values in it
 * at all, which is the point: dialing a beat is a token edit, never a JS edit.
 *
 * WHY THIS FILE EXISTS RATHER THAN `animation-timeline: scroll()`. The native
 * feature does all of the above with no script. It is not in release Firefox,
 * and Kyle uses Firefox. Measured on his own build, 2026-08-04, Firefox
 * 153.0.1: CSS.supports('animation-timeline', 'scroll()') === false and
 * 'ScrollTimeline' in window === false. MDN's compat data agrees (firefox:
 * "preview" — Nightly only). Writing this the native way with a JS fallback
 * would mean maintaining two implementations of which Kyle only ever sees one.
 *
 * MOTION PREFERENCE IS NOT CHECKED HERE, ON PURPOSE. Splitting text is not
 * motion, and a progress number is just a number. Each section's CSS gates
 * every animated declaration behind `@media (prefers-reduced-motion:
 * no-preference)`, so that media query is the single arbiter — including when
 * the user flips the OS setting mid-session, which a JS-side check would miss
 * without a listener nobody would remember to add.
 *
 * FAIL-VISIBLE. `o.ready` is only set after the split succeeds. If this script
 * never runs, or throws, the CSS motion rules never match and the copy renders
 * in its finished state — every line visible, every letter at its destination
 * colour. There is no state of either panel in which the copy is invisible and
 * stays that way.
 *
 * THIS FILE DEFINES AND DOES NOTHING ELSE. Two sections may both request it in
 * one document; `defer` scripts execute once per tag, so it will run twice. It
 * is a pure definition with no side effects, so the second run simply replaces
 * the first with an identical object. The mounts live in the per-section
 * adapters (eg-home-panel.js, eg-home-builtline.js) and each names its own
 * host.
 */
window.EgScrollSweep = (function () {
  /* Split ONE group's lines into characters, with ONE running index across all
     of them.

     Lines are separate blocks in the markup (the breaks are rhetorical — see
     the sections' Liquid), so each is split in place and keeps its own box. The
     index does NOT restart per line: it is what orders the recolour, and the
     wave has to cross from the end of one line to the start of the next without
     a seam. It DOES restart per GROUP, because a group is a stanza that gets
     its own sweep, its own slice of the scroll, and its own fade.

     Spaces stay BARE TEXT NODES between the spans rather than becoming spans of
     their own. They are the line-break opportunities that let a line wrap
     inside itself on a phone, and wrapping them would (a) make them candidates
     for the recolour, which is meaningless on whitespace, and (b) risk an
     engine treating the span boundary rather than the space as the break point.
     Only real characters get an index, so the wave is evenly spaced across
     LETTERS, not across letters-and-gaps. */
  function split(group, o) {
    var lines = group.querySelectorAll(o.line);
    if (!lines.length) return false;

    var index = 0;
    for (var l = 0; l < lines.length; l += 1) {
      var text = lines[l].textContent;
      var ink = document.createElement('span');
      ink.className = o.inkClass;

      for (var i = 0; i < text.length; i += 1) {
        var ch = text.charAt(i);
        if (ch === ' ' || ch === '\n' || ch === '\t') {
          ink.appendChild(document.createTextNode(' '));
          continue;
        }
        var span = document.createElement('span');
        span.className = o.charClass;
        span.textContent = ch;
        span.style.setProperty(o.index, String(index));
        ink.appendChild(span);
        index += 1;
      }

      lines[l].textContent = '';
      lines[l].appendChild(ink);
    }

    /* The DIVISOR is the LAST index, not the count — the thresholds run
       i / n across 0…1, so the final character has to land on 1 exactly or the
       sweep finishes before its own end. Floored at 1 so a one-character
       stanza cannot divide by zero. */
    group.style.setProperty(o.count, String(Math.max(index - 1, 1)));
    return true;
  }

  function mount(o) {
    var host = document.querySelector(o.host);
    if (!host) return;

    var groups = host.querySelectorAll(o.group);
    if (!groups.length) return;

    for (var g = 0; g < groups.length; g += 1) {
      if (!split(groups[g], o)) return;
      /* Optional: a single-stanza panel has nothing to slice, so it does not
         ask for these and no dead properties get written. */
      if (o.groupIndex) groups[g].style.setProperty(o.groupIndex, String(g));
      if (o.groupCount) groups[g].style.setProperty(o.groupCount, String(groups.length));
    }

    /* Progress is measured off the HOST (the tall track), not the sticky panel
       inside it — the panel's own rect stops moving the moment it pins, which
       is precisely when this needs to start counting. `offsetTop` is no use
       here for the same reason: on a stuck element it reports the STUCK
       position, so the maths has to come off a plain relatively-positioned
       track.

         travel = trackHeight - viewportHeight   the pinned scroll budget
         p      = -rect.top / travel             0 when the panel has just
                                                 finished covering what is
                                                 behind it, 1 at the end of the
                                                 track

       Clamped, so p is 0 for the whole approach (the panel rises with nothing
       on it — the dictated blank arrival) and 1 for anything past the track. */
    function progress() {
      var rect = host.getBoundingClientRect();
      var travel = rect.height - window.innerHeight;
      if (travel <= 0) return 1;
      var p = -rect.top / travel;
      return p < 0 ? 0 : p > 1 ? 1 : p;
    }

    /* rAF-coalesced. `scroll` fires far more often than the compositor paints,
       and every handler here ends in a style write — writing more than once per
       frame is pure waste, and on a trackpad it is a lot of waste. */
    var queued = false;

    function commit() {
      queued = false;
      host.style.setProperty(o.progress, progress().toFixed(4));
    }

    function schedule() {
      if (queued) return;
      queued = true;
      requestAnimationFrame(commit);
    }

    /* `passive` because this never calls preventDefault — without it the
       browser has to wait for the handler before it can scroll, which is
       exactly the jank this whole file is trying not to introduce. */
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    commit();
    host.setAttribute(o.ready, '');

    /* THE ARRIVAL FADE IS NOT SCROLL-DRIVEN AND MUST NOT BECOME SO (Kyle,
       2026-08-04 third pass). It fires once, the moment the host's top edge
       reaches the top of the window, and then it is a plain CSS animation on a
       clock.

       What this replaced: the fade used to read the progress property like the
       recolour does, so its head start was spent in SCROLL DISTANCE — ~175px of
       wheel at a completely blank screen before anything appeared. Revealing
       something should not cost the reader distance; scrubbing something
       should.

       THE ROOT IS A ONE-PIXEL LINE AT THE TOP OF THE VIEWPORT. `bottom: -100%`
       collapses the intersection rectangle to the top edge, so the host counts
       as intersecting only once its own top has reached y=0 — which is exactly
       the moment the sticky panel pins and covers what is behind it. A plain
       observer with no rootMargin would fire when the panel merely became
       visible at the BOTTOM of the window, a whole viewport too early.

       IT DISCONNECTS AFTER FIRING, and that is the answer to "scroll up and
       back down". The attribute is set once and never removed, so the hidden
       rule stops matching forever: the copy does not blink out and re-fade on
       every pass, and it can never be left stuck invisible at a scroll position
       nobody can find.

       No IntersectionObserver (an old engine, or a polyfill that never loaded)
       means arriving immediately rather than never. The failure mode is copy
       that skipped its entrance, not a blank panel. */
    function arrive() {
      host.setAttribute(o.arrived, '');
    }

    if (typeof IntersectionObserver !== 'function') {
      arrive();
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        for (var e = 0; e < entries.length; e += 1) {
          if (entries[e].isIntersecting) {
            arrive();
            observer.disconnect();
            return;
          }
        }
      },
      { rootMargin: '0px 0px -100% 0px' },
    );
    observer.observe(host);
  }

  return { mount: mount };
})();
